import React, { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, setDoc, getDocs, onSnapshot, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from './firebase';
import { heartbeatDoc, usersColl } from './db';
import { usePushNotification } from './hooks/usePushNotification';
import Login from './components/Login';
import Home from './components/Home';
import DailyFlow from './components/DailyFlow';
import PhotoArchive from './components/PhotoArchive';
import DailyQuestion from './components/DailyQuestion';
import NightNote from './components/NightNote';
import Tasks from './components/Tasks';
import MusicBox from './components/MusicBox';
import Countdown from './components/Countdown';
import ImHere from './components/ImHere';
import MoreScreen from './components/MoreScreen';
import BondScreen from './components/BondScreen';
import MemoriesScreen from './components/MemoriesScreen';
import OurLittleOne from './components/OurLittleOne';
import MoodSync from './components/MoodSync';
import BottomNav from './components/BottomNav';
import StreakScreen from './components/StreakScreen';
import Settings from './components/Settings';
import MemoryTimeline from './components/MemoryTimeline';
import TimeCapsule from './components/TimeCapsule';
import LocationMap from './components/LocationMap';
import DateIdeas from './components/DateIdeas';
import GiftWishlist from './components/GiftWishlist';
import CycleTracker from './components/CycleTracker';
import Welcome from './pages/Welcome';
import Setup from './pages/Setup';
import AdminPanel from './pages/AdminPanel';
import CharacterCreator from './pages/CharacterCreator';
import { ProfileGender } from './utils/gender';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  coupleId: string;
  partnerUid?: string;
  gender?: ProfileGender;
  seenMinikPresets?: boolean;
}

export type Screen =
  | 'home' | 'daily-flow' | 'bond' | 'memories' | 'us' | 'photos' | 'question' | 'notes' | 'more'
  | 'tasks' | 'music' | 'countdown' | 'imhere'
  | 'pet' | 'mood' | 'streak' | 'settings' | 'character-creator'
  | 'timeline' | 'capsule' | 'map' | 'date-ideas' | 'wishlist' | 'cycle';

// Read URL path once at startup
const rawUrlPath = window.location.pathname.slice(1); // e.g. "" | "@emre-hande" | "admin"
const urlPath = rawUrlPath.startsWith('@') ? rawUrlPath.slice(1) : rawUrlPath;
const isAdminRoute = urlPath === 'admin';
const urlCoupleId = !isAdminRoute && urlPath ? urlPath : null;

function getDisplayName(email: string): string {
  const e = email.toLowerCase();
  if (e.includes('serhat')) return 'Serhat';
  if (e.includes('ayca') || e.includes('ayça')) return 'Ayça';
  const prefix = e.split('@')[0].replace(/[._\-0-9]/g, ' ').trim();
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

function App() {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState<Screen>('home');
  const [previousScreen, setPreviousScreen] = useState<Screen | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const screenRef = useRef<HTMLDivElement | null>(null);

  const { init: initPush } = usePushNotification(userProfile);

  useEffect(() => {
    if (!userProfile) return;
    const t = setTimeout(initPush, 4000);
    return () => clearTimeout(t);
  }, [userProfile?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (u) {
        const snap = await getDoc(doc(db, 'users', u.uid));

        let coupleId: string;
        let displayName: string;
        let fromInvite = false;

        if (snap.exists() && snap.data().coupleId) {
          // Existing user — already set up
          coupleId = snap.data().coupleId;
          displayName = snap.data().displayName || getDisplayName(u.email || '');
        } else {
          // New user — check invite list
          const email = (u.email || '').toLowerCase().replace(/\//g, '_');
          const inviteSnap = await getDoc(doc(db, 'invites', email));

          if (inviteSnap.exists()) {
            const inv = inviteSnap.data();
            coupleId = inv.coupleId;
            displayName = inv.displayName || getDisplayName(u.email || '');
            fromInvite = true;
            // Clean up the invite
            await deleteDoc(doc(db, 'invites', email));
          } else {
            // No invite — fall back to uid as coupleId (existing behaviour for manual setup)
            coupleId = snap.exists() ? (snap.data().coupleId || u.uid) : u.uid;
            displayName = snap.exists() ? (snap.data().displayName || getDisplayName(u.email || '')) : getDisplayName(u.email || '');
          }
        }

        // Find partner
        const allUsers = await getDocs(usersColl());
        const partnerUid = allUsers.docs.find(d => d.id !== u.uid && d.data().coupleId === coupleId)?.id;

        if (snap.exists() && !fromInvite) {
          const data = snap.data();
          setUserProfile({ ...data, coupleId, partnerUid, displayName } as UserProfile);
          // setupComplete === false means they need the wizard
          if (data.setupComplete === false) setNeedsSetup(true);
        } else {
          // New user (from invite or first-time)
          const profile: UserProfile = { uid: u.uid, email: u.email || '', displayName, coupleId, partnerUid };
          await setDoc(doc(db, 'users', u.uid), {
            ...profile,
            createdAt: serverTimestamp(),
            // Mark as needing setup only for invite-based users
            setupComplete: fromInvite ? false : true,
          });
          setUserProfile(profile);
          if (fromInvite) setNeedsSetup(true);
        }

        // Dismiss the welcome screen once the user is authenticated
        setWelcomeDismissed(true);
      } else {
        setUserProfile(null);
        setNeedsSetup(false);
      }
      setFirebaseUser(u);
      setLoading(false);
    });
  }, []);

  // Heartbeat listener
  useEffect(() => {
    if (!userProfile) return;
    const unsub = onSnapshot(heartbeatDoc(userProfile.coupleId, userProfile.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (!data.unread) return;
      showToast(`${data.fromName} seni düşündü`);
      updateDoc(heartbeatDoc(userProfile.coupleId, userProfile.uid), { unread: false });
    });
    return unsub;
  }, [userProfile]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const navigate = (s: Screen) => {
    screenRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (s !== screen) setPreviousScreen(screen);
    setScreen(s);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      screenRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [screen]);

  // ── Admin route ──
  if (isAdminRoute) return <AdminPanel />;

  // ── Loading ──
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="heart-pulse" />
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Yükleniyor...</p>
      </div>
    );
  }

  // ── Welcome screen (URL has coupleId, user not yet logged in) ──
  if (urlCoupleId && !welcomeDismissed && !firebaseUser) {
    return <Welcome coupleId={urlCoupleId} onStart={() => setWelcomeDismissed(true)} />;
  }

  // ── Login ──
  if (!firebaseUser) return <Login />;
  if (!userProfile) return <div className="loading-screen"><div className="heart-pulse" /></div>;

  // ── Onboarding Setup ──
  if (needsSetup) {
    const handleSetupComplete = async () => {
      // Re-read user doc so displayName/avatar changes from wizard are reflected immediately
      if (firebaseUser) {
        try {
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (snap.exists() && snap.data().displayName) {
            const data = snap.data();
            setUserProfile(prev => prev ? { ...prev, displayName: data.displayName, gender: data.gender } : prev);
          }
        } catch { /* silent */ }
      }
      setNeedsSetup(false);
    };

    return (
      <AnimatePresence>
        <motion.div key="setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Setup user={userProfile} onComplete={handleSetupComplete} />
        </motion.div>
      </AnimatePresence>
    );
  }

  const renderScreen = () => {
    switch (screen) {
      case 'home': return <Home user={userProfile} navigate={navigate} />;
      case 'daily-flow': return <DailyFlow user={userProfile} navigate={navigate} />;
      case 'bond': return <BondScreen user={userProfile} navigate={navigate} />;
      case 'memories': return <MemoriesScreen user={userProfile} navigate={navigate} />;
      case 'us': return <MoreScreen user={userProfile} navigate={navigate} />;
      case 'photos': return <PhotoArchive user={userProfile} navigate={navigate} />;
      case 'question': return <DailyQuestion user={userProfile} navigate={navigate} backTo={previousScreen || 'bond'} />;
      case 'notes': return <NightNote user={userProfile} navigate={navigate} backTo={previousScreen || 'memories'} />;
      case 'more': return <MoreScreen user={userProfile} navigate={navigate} />;
      case 'tasks': return <Tasks user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'music': return <MusicBox user={userProfile} navigate={navigate} />;
      case 'countdown': return <Countdown user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'imhere': return <ImHere user={userProfile} navigate={navigate} showToast={showToast} backTo={previousScreen || 'bond'} />;
      case 'pet': return <OurLittleOne user={userProfile} navigate={navigate} backTo={previousScreen || 'home'} />;
      case 'mood': return <MoodSync user={userProfile} navigate={navigate} backTo={previousScreen || 'bond'} />;
      case 'streak': return <StreakScreen user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'timeline': return <MemoryTimeline user={userProfile} navigate={navigate} />;
      case 'capsule': return <TimeCapsule user={userProfile} navigate={navigate} />;
      case 'map': return <LocationMap user={userProfile} navigate={navigate} />;
      case 'date-ideas': return <DateIdeas user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'wishlist': return <GiftWishlist user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'cycle': return <CycleTracker user={userProfile} navigate={navigate} backTo={previousScreen || 'us'} />;
      case 'settings': return (
        <Settings
          user={userProfile}
          navigate={navigate}
          onProfileUpdate={(updated) => setUserProfile(prev => prev ? { ...prev, ...updated } : prev)}
        />
      );
      case 'character-creator': return (
        <CharacterCreator
          user={userProfile}
          navigate={navigate}
          mode="recreate"
          onComplete={() => navigate('pet')}
        />
      );
    }
  };

  return (
    <div className="app">
      {/* Animated toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className="toast"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        key={screen}
        ref={screenRef}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 110, damping: 24, mass: 0.8 }}
        className="screen"
      >
        {renderScreen()}
      </motion.div>

      <BottomNav current={screen} navigate={navigate} />
    </div>
  );
}

export default App;
